import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { environment } from '../environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RouteReuseStrategy } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { APP_CONFIG, appConfig } from './app.config';
import { WdDevWarningBannerComponent, WdNavbarHeightDirective, WdConnectionStatusComponent, WdGpsStatusComponent, WdStorageStatusComponent, WdErrorBadgeComponent } from '@luzula/luzula-lib';
import { AppRoutingModule } from './app-routing.module';
import { NavbarComponent } from './navbar/navbar.component';
import { IconsModule } from './icons/icons.module';

// App-Komponenten
import { AlleBohrpunkteComponent } from './alle-bohrpunkte/alle-bohrpunkte.component';
import { AuthButtonComponent } from './navbar/auth-button/auth-button.component';
import { ListenverwaltungComponent } from './listenverwaltung/listenverwaltung.component';
import { HandbuchComponent } from './handbuch/handbuch.component';
import { ChangelogComponent } from './changelog/changelog.component';
import { PwaInstallBannerComponent } from './pwa-install-banner/pwa-install-banner.component';

// Bohrpunkte-Komponenten (Barrel-Import)
import {
	BohrpunktComponent,
	BohrpunktTabsComponent,
	BohrpunktNeuComponent,
	BohrpunktNummerComponent,
	BohrpunktPlausiComponent,
	BohrpunktPrintViewComponent,
	MultiPrintViewComponent,
	BohrpunktAllgemeinComponent,
	BohrpunktBodenComponent,
	BohrpunktKurzprotokollComponent,
	BohrpunktVegetationComponent,
	BohrpunktWeitereComponent,
	BohrpunktMapComponent,
	HlAreaDirective,
	HlAreaComponent,
	HlAreaContainerComponent,
	AdresseComponent,
	AufnahmeComponent,
	BemerkungenComponent,
	BodenComponent,
	KurzprotokollComponent,
	NaturraumComponent,
	SubstrateHorizonteComponent,
	VegetationsaufnahmeComponent,
	BohrpunktListComponent,
	BohrpunktListPanelComponent,
	ErrorComponent,
	SandboxControlsComponent,
} from './bohrpunkte';

// Controls
import { LuzulaControlsModule, MapModule, MapStatusService, StorageService } from '@luzula/luzula-lib';
import { HelpModule } from './bohrpunkte/help/help.module';

// Services
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { initServicesFactory } from './init-services.factory';
import { FilterService } from './filter.service';
import { EvaluationService } from './bohrpunkte/evaluation.service';
import { OptionService } from './bohrpunkte/option.service';
import { ValidationService } from './bohrpunkte/validation.service';
import { ConnectionService, GpsService, ViewportService, OfflineModeService, MdbService, UpdateService, WdMapCacheRouteReuseStrategy, SplashScreenService, GlobalTileSeedService, MBTilesService } from '@luzula/luzula-lib';
import { ProjectService } from './project.service';

// Keycloak
import { provideKeycloakAngular } from './keycloak.config';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { errorCaptureInterceptor } from '@luzula/luzula-lib';


// App-Service Provider (provideLuzulaApp)
import { APP_PROVIDERS } from './core/app-providers';

// Pipes
import { MaskForGuestPipe } from '@luzula/luzula-lib';

@NgModule({
	bootstrap: [AppComponent],
	declarations: [
		// App-Komponenten
		AppComponent,
		AlleBohrpunkteComponent,
		AuthButtonComponent,
		NavbarComponent,
		ListenverwaltungComponent,
		ChangelogComponent,

		// Bohrpunkte-Komponenten (ehemals BohrpunkteModule)
		BohrpunktComponent,
		BohrpunktPlausiComponent,
		BohrpunktPrintViewComponent,
		MultiPrintViewComponent,
		BohrpunktTabsComponent,
		BohrpunktListComponent,
		BohrpunktNummerComponent,
		BohrpunktKurzprotokollComponent,
		BohrpunktAllgemeinComponent,
		BohrpunktVegetationComponent,
		BohrpunktBodenComponent,
		BohrpunktWeitereComponent,
		BohrpunktMapComponent,
		BohrpunktNeuComponent,

		// HL-Area-Komponenten
		HlAreaDirective,
		HlAreaComponent,
		HlAreaContainerComponent,
		AdresseComponent,
		AufnahmeComponent,
		BemerkungenComponent,
		BodenComponent,
		KurzprotokollComponent,
		NaturraumComponent,
		SubstrateHorizonteComponent,
		VegetationsaufnahmeComponent,

		// Weitere Bohrpunkte-Komponenten
		BohrpunktListPanelComponent,
		ErrorComponent,
		SandboxControlsComponent
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		CommonModule,
		FormsModule,
		IconsModule,
		MapModule,
		LuzulaControlsModule,
		HelpModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: true,
			registrationStrategy: 'registerImmediately'
		}),
		ToastrModule.forRoot({
			timeOut: 3000,
			positionClass: 'toast-bottom-right',
			preventDuplicates: true,
			progressBar: true
		}),
		NgbModule,
		NgbTooltipModule,
		HandbuchComponent,
		WdDevWarningBannerComponent,
		WdNavbarHeightDirective,
		WdConnectionStatusComponent,
		WdGpsStatusComponent,
		WdStorageStatusComponent,
		WdErrorBadgeComponent,
		MaskForGuestPipe,
		PwaInstallBannerComponent
	],
	providers: [
		{ provide: APP_CONFIG, useValue: appConfig },
		provideHttpClient(withInterceptors([includeBearerTokenInterceptor, errorCaptureInterceptor])),
		provideKeycloakAngular(),
		provideAppInitializer(() => {
			const initializerFn = (initServicesFactory)(inject(ConnectionService), inject(UpdateService), inject(GpsService), inject(AuthService), inject(DataService), inject(ProjectService), inject(EvaluationService), inject(FilterService), inject(MapStatusService), inject(MdbService), inject(OfflineModeService), inject(OptionService), inject(StorageService), inject(ValidationService), inject(ViewportService), inject(SplashScreenService), inject(GlobalTileSeedService), inject(MBTilesService));
			return initializerFn();
		}),
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: RouteReuseStrategy, useClass: WdMapCacheRouteReuseStrategy },
		...APP_PROVIDERS
	]
})
export class AppModule { }
